import {
  type ComponentProps,
  type FC,
  memo,
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import { isEqual } from "lodash-es";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Button, ButtonKind, ButtonVariant } from "@stubhub/broadway-web";
import { Spinner } from "@stubhub/broadway-web/icons";
import { useFeatureParticipationRecorder, useSimpleFeatureTernary } from "@viagogo/feature";
import { Content } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useIsFirstRender } from "src/shared/hooks/useIsFirstRender";
import { usePrevious } from "src/shared/hooks/usePrevious";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import { MobileCalendar } from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/MobileCalendar";
import {
  bottomButtonContainer,
  containerStyle,
  fullWidthButton,
  mobileCalendarBody,
  seeMoreButton,
  seeMoreContainer,
} from "src/website-apps/responsive/stubhub-performer/components/calendar/styles";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { NotificationOptionEnum, useStoredNotifications } from "src/website-apps/shared/components/notifications";
import { NotificationModal } from "src/website-apps/shared/components/on-sale-notifications/NotificationModal";
import type { CalendarRenderMode } from "src/website-apps/shared/constants/calendar-render-mode";
import { PerformerViewMode } from "src/website-apps/shared/constants/performer-view-mode";
import { useDateCookieOptions } from "src/website-apps/shared/context/date-context";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useStableNow } from "src/website-apps/shared/hooks/useStableNow";
import { getFirstAndLastDayOfMonth, getMonthDateKey } from "src/website-apps/shared/utils/calendar";
import { CalendarHeader } from "./CalendarHeader";
import { CalendarMonth } from "./CalendarMonth";
import { CalendarSkeleton } from "./CalendarSkeleton";
import { ResidencyDesktopFilterBar } from "./residency/ResidencyDesktopFilterBar";
import { ResidencyMonthNavigation } from "./residency/ResidencyMonthNavigation";
import { isMonthInPast } from "./utils";

const seeMoreButtonClasses: ComponentProps<typeof Button>["classes"] = { root: seeMoreButton };

interface Props {
  renderMode: CalendarRenderMode | null;
}

/**
 * Main calendar component that orchestrates the display of a monthly calendar view with event data.
 * Handles month navigation, event loading, and renders appropriate date cards based on the sport/league context.
 * It currently supports MLB-specific rendering with fallback to generic date cards for other sports.
 * @returns A complete calendar view with header, navigation, and date cards
 */
export const Calendar: FC<Props> = memo(({ renderMode }) => {
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const model = usePerformerPageModelData();
  const [
    state,
    dispatch,
    { refreshCalendarEvents, prefetchCalendarEvents, extendDateRange, resetAllFiltersExcludingLocation },
  ] = useEventsGridContext();
  const dateCookieOptions = useDateCookieOptions();
  const { calendarMonthsWithEventsAsDates, calendarEventsByMonthAndDay, calendarHasMoreMonths } = state;
  // Defer heavy prop updates to reduce synchronous render pressure
  const deferredMonths = useDeferredValue(calendarMonthsWithEventsAsDates);
  const deferredEventsByMonthAndDay = useDeferredValue(calendarEventsByMonthAndDay);
  const events = useMemo(
    () => state.calendarEvents?.pagedEvents?.items ?? [],
    [state.calendarEvents?.pagedEvents?.items],
  );
  const { filters, calendarEvents } = state;
  type ResidencyCalendarSnapshot = {
    monthDate: Date;
    monthKey: string;
    eventsByMonthAndDay: typeof deferredEventsByMonthAndDay;
    calendarEvents: NonNullable<typeof calendarEvents>;
  };
  const previousFilters = usePrevious(filters);
  const venueId = useMemo(() => filters.venueIdFilterType ?? undefined, [filters.venueIdFilterType]);
  const calendarPrefetchStateRef = useRef({
    calendarEventsByMonth: state.calendarEventsByMonth,
    queryParams: state.queryParams,
  });

  useEffect(() => {
    calendarPrefetchStateRef.current = {
      calendarEventsByMonth: state.calendarEventsByMonth,
      queryParams: state.queryParams,
    };
  }, [state.calendarEventsByMonth, state.queryParams]);

  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const isTablet = useMediaQuery(Breakpoint.Tablet) && !isDesktop;
  const isResidency = renderMode === PerformerViewMode.Residency;
  const isDesktopOrResidencyTablet = isDesktop || (isResidency && isTablet);

  // Residency uses the backend month index for picker/navigation options, then loads
  // selected month event data on demand.
  const residencyAvailableMonths = useMemo(() => {
    if (!isResidency || !calendarEvents?.monthsWithEvents) return calendarMonthsWithEventsAsDates;
    return calendarEvents.monthsWithEvents
      .map((monthKey) => dayjs(`${monthKey}-01`).startOf("month").toDate())
      .filter((monthDate) => !Number.isNaN(monthDate.getTime()));
  }, [isResidency, calendarMonthsWithEventsAsDates, calendarEvents]);

  // For residency mode: track one visible month at a time. Defaults to the nearest
  // month with events on or after today; falls back to the first available month.
  const [currentResidencyMonth, setCurrentResidencyMonth] = useState<Date | null>(null);
  const today = useStableNow();

  const resolvedResidencyMonth = useMemo(() => {
    if (residencyAvailableMonths.length === 0) return null;
    if (currentResidencyMonth !== null) {
      const stillAvailable = residencyAvailableMonths.some((m) => dayjs(m).isSame(currentResidencyMonth, "month"));
      if (stillAvailable) return currentResidencyMonth;
    }
    const nearestFuture = residencyAvailableMonths.find((m) => !isMonthInPast(today, m));
    return nearestFuture ?? residencyAvailableMonths[0] ?? null;
  }, [residencyAvailableMonths, currentResidencyMonth, today]);

  const lastRequestedResidencyMonthKeyRef = useRef<string | null>(null);
  const loadResidencyMonth = useCallback(
    (month: Date) => {
      lastRequestedResidencyMonthKeyRef.current = getMonthDateKey(month);
      const [from, to] = getFirstAndLastDayOfMonth(month);
      refreshCalendarEvents(
        state,
        dispatch,
        {
          ...filters,
          pageIndex: 0,
        },
        from.toISOString(),
        to.toISOString(),
        venueId,
      );
    },
    [dispatch, filters, refreshCalendarEvents, state, venueId],
  );

  const handleResidencyMonthSelect = useCallback(
    (month: Date) => {
      setCurrentResidencyMonth(month);
      loadResidencyMonth(month);
    },
    [loadResidencyMonth],
  );

  const prefetchResidencyMonth = useCallback(
    (month: Date) => {
      const [from, to] = getFirstAndLastDayOfMonth(month);
      return prefetchCalendarEvents(
        calendarPrefetchStateRef.current.calendarEventsByMonth,
        calendarPrefetchStateRef.current.queryParams,
        dispatch,
        {
          ...filters,
          pageIndex: 0,
        },
        from.toISOString(),
        to.toISOString(),
        venueId,
      );
    },
    [dispatch, filters, prefetchCalendarEvents, venueId],
  );

  const handleResidencyPrev = useCallback(() => {
    if (resolvedResidencyMonth === null) return;
    const currentIdx = residencyAvailableMonths.findIndex((m) => dayjs(m).isSame(resolvedResidencyMonth, "month"));
    const prev = residencyAvailableMonths[currentIdx - 1];
    if (prev) {
      handleResidencyMonthSelect(prev);
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.RESIDENCY_CALENDAR_PREV_PAGE_CLICK);
    }
  }, [handleResidencyMonthSelect, residencyAvailableMonths, resolvedResidencyMonth, storePageEvent]);

  const handleResidencyNext = useCallback(() => {
    if (resolvedResidencyMonth === null) return;
    const currentIdx = residencyAvailableMonths.findIndex((m) => dayjs(m).isSame(resolvedResidencyMonth, "month"));
    const next = residencyAvailableMonths[currentIdx + 1];
    if (next) {
      handleResidencyMonthSelect(next);
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.RESIDENCY_CALENDAR_NEXT_PAGE_CLICK);
    }
  }, [handleResidencyMonthSelect, residencyAvailableMonths, resolvedResidencyMonth, storePageEvent]);

  // Notification modal state and handlers
  const [notificationModalOpenEventId, setNotificationModalOpenEventId] = useState<number | null>(null);
  const storedNotifications = useStoredNotifications();
  const isStubhub = useIsStubhub();
  // Variant of TMInternationalStopUkRedirect stops the GB→stubhub.co.uk redirect; record:false keeps that experiment's participation on the grid items.
  const ukRedirectStopped =
    useSimpleFeatureTernary(FeatureSetEnum.TMInternationalStopUkRedirect, false, true, () => isStubhub, false) ?? false;
  const recordSoldOutBannerParticipation = useFeatureParticipationRecorder(
    FeatureSetEnum.TMSoldOutBannerSkipRecentlySoldLookup,
  );

  const closeNotificationHandler = useCallback(() => {
    setNotificationModalOpenEventId(null);
  }, []);

  const openNotificationHandler = useCallback(
    (eventId: number) => {
      const isStored = storedNotifications.isNotificationStored(eventId, NotificationOptionEnum.EVENT);
      if (!isStored) {
        const event = events.find((e) => e.eventId === eventId);
        // Mirror useNotificationModal: GB events on StubHub redirect away and must not open the modal.
        if (isStubhub && !ukRedirectStopped && event?.countryCode === "GB") {
          return;
        }
        setNotificationModalOpenEventId(eventId);
        // Only record where the backend stamped the experiment copy; the calendar opens the modal for cards without it too.
        if (event?.notificationModalContent?.hasValue) {
          recordSoldOutBannerParticipation();
        }
        storePageEvent(PE_TYPE.CLICK, PE_KEYS.PERFORMER_NOTIFICATIONS_V2_OPEN, String(eventId));
      }
    },
    [storedNotifications, storePageEvent, recordSoldOutBannerParticipation, events, isStubhub, ukRedirectStopped],
  );

  // Render notification modal
  const notificationModal = useMemo(() => {
    if (!notificationModalOpenEventId) {
      return null;
    }
    const event = events.find((e) => e.eventId === notificationModalOpenEventId);
    return (
      event && (
        <NotificationModal
          isOpen
          onClose={closeNotificationHandler}
          userFirstName={appContext.userFirstName ?? ""}
          userEmailAddress={appContext.userEmailAddress ?? ""}
          performerName={model.categorySummary.categoryName}
          entityId={event.eventId}
          notificationModalContent={event.notificationModalContent}
        />
      )
    );
  }, [
    notificationModalOpenEventId,
    events,
    closeNotificationHandler,
    appContext.userFirstName,
    appContext.userEmailAddress,
    model.categorySummary.categoryName,
  ]);

  // Track the number of events loaded
  useEffect(() => {
    if (!calendarEvents?.isUpdating) {
      storePageEvent(PE_TYPE.LOADED, PE_KEYS.EVENTS_CALENDAR_NUM_EVENTS, String(events.length));
    }
  }, [calendarEvents?.isUpdating, events.length, storePageEvent]);

  // Fetch events when filters change or on first render
  const isFirstRender = useIsFirstRender();
  useEffect(() => {
    const hasDateFilter = filters.dates?.dates.from && filters.dates.dates.to;
    const previousHadDateFilter = previousFilters?.dates?.dates.from && previousFilters.dates.dates.to;
    const filtersChanged = !!previousFilters && !isEqual(previousFilters, filters);

    // Skip if no changes and not first render
    if (!filtersChanged && !isFirstRender) return;

    // If date filter was cleared, fetch all available months
    if (previousHadDateFilter && !hasDateFilter) {
      refreshCalendarEvents(state, dispatch, filters, undefined, undefined, venueId);
      return;
    }

    // If we have months to display, fetch events for those months
    if (calendarMonthsWithEventsAsDates.length > 0) {
      const [firstMonth] = calendarMonthsWithEventsAsDates;
      const lastMonth = calendarMonthsWithEventsAsDates.at(-1);

      if (firstMonth && lastMonth) {
        const [from] = getFirstAndLastDayOfMonth(firstMonth);
        const [, to] = getFirstAndLastDayOfMonth(lastMonth);

        const hasInitialEvents = events.length > 0;
        if (!hasInitialEvents) {
          refreshCalendarEvents(state, dispatch, filters, from.toISOString(), to.toISOString(), venueId);
        }
      }
    } else if (!hasDateFilter) {
      // No months and no date filter - fetch default months
      refreshCalendarEvents(state, dispatch, filters, undefined, undefined, venueId);
    }
  }, [
    calendarMonthsWithEventsAsDates,
    events.length,
    filters,
    isFirstRender,
    refreshCalendarEvents,
    state,
    dispatch,
    venueId,
    previousFilters,
  ]);

  useEffect(() => {
    if (!isResidency || !isDesktopOrResidencyTablet || resolvedResidencyMonth === null || events.length === 0) return;
    if (calendarEvents?.isUpdating) return;

    const resolvedMonthKey = getMonthDateKey(resolvedResidencyMonth);
    const isResolvedMonthLoaded = calendarMonthsWithEventsAsDates.some((monthDate) =>
      dayjs(monthDate).isSame(resolvedResidencyMonth, "month"),
    );

    if (isResolvedMonthLoaded) {
      if (lastRequestedResidencyMonthKeyRef.current === resolvedMonthKey) {
        lastRequestedResidencyMonthKeyRef.current = null;
      }
      return;
    }

    if (lastRequestedResidencyMonthKeyRef.current === resolvedMonthKey) return;

    loadResidencyMonth(resolvedResidencyMonth);
  }, [
    calendarEvents?.isUpdating,
    calendarMonthsWithEventsAsDates,
    events.length,
    isDesktopOrResidencyTablet,
    isResidency,
    loadResidencyMonth,
    resolvedResidencyMonth,
  ]);

  useEffect(() => {
    if (
      !isResidency ||
      !isDesktopOrResidencyTablet ||
      resolvedResidencyMonth === null ||
      events.length === 0 ||
      calendarEvents?.isUpdating ||
      residencyAvailableMonths.length <= 1
    ) {
      return;
    }

    let isCancelled = false;
    const monthsToPrefetch = residencyAvailableMonths.filter(
      (monthDate) => !dayjs(monthDate).isSame(resolvedResidencyMonth, "month"),
    );

    const prefetchResidencyMonths = async () => {
      for (const month of monthsToPrefetch) {
        if (isCancelled) return;
        await prefetchResidencyMonth(month);
      }
    };

    prefetchResidencyMonths().catch(() => {
      // Prefetch is opportunistic; foreground month loads still surface errors.
    });

    return () => {
      isCancelled = true;
    };
  }, [
    calendarEvents?.isUpdating,
    events.length,
    isDesktopOrResidencyTablet,
    isResidency,
    prefetchResidencyMonth,
    residencyAvailableMonths,
    resolvedResidencyMonth,
  ]);

  const [initalLoadTime] = useState(() => Date.now());
  const calendarLoadTimeRef = useRef(initalLoadTime);

  const handleEventClick = useCallback(() => {
    if (calendarLoadTimeRef.current === -1) {
      // Ensure we only log once per load
      return;
    }
    const timeToSelection = Date.now() - calendarLoadTimeRef.current;
    storePageEvent(PE_TYPE.CLICK, PE_KEYS.CALENDAR_TIME_TO_SELECTION, String(timeToSelection));
    calendarLoadTimeRef.current = -1;
  }, [storePageEvent]);

  const seeMoreClickCountRef = useRef(0);

  const handleResetFilters = useCallback(() => {
    resetAllFiltersExcludingLocation(dispatch, state, false, dateCookieOptions);
  }, [dateCookieOptions, dispatch, resetAllFiltersExcludingLocation, state]);

  const handleSeeMore = useCallback(() => {
    // Schedule heavy calendar updates at a lower priority without altering outcome
    startTransition(() => {
      const monthsToExtend = isDesktop ? 2 : 1;
      extendDateRange(state, dispatch, calendarMonthsWithEventsAsDates, monthsToExtend);
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.CALENDAR_SEE_MORE, String(seeMoreClickCountRef.current));
      seeMoreClickCountRef.current += 1;
    });
  }, [isDesktop, extendDateRange, state, dispatch, calendarMonthsWithEventsAsDates, storePageEvent]);

  // For residency: show only the currently-selected month. For other modes: show all loaded months.
  const visibleMonths = useMemo(() => {
    if (!isResidency || resolvedResidencyMonth === null) return deferredMonths;
    return deferredMonths.filter((m) => dayjs(m).isSame(resolvedResidencyMonth, "month"));
  }, [isResidency, resolvedResidencyMonth, deferredMonths]);

  const [residencyCalendarSnapshot, setResidencyCalendarSnapshot] = useState<ResidencyCalendarSnapshot | null>(null);

  useEffect(() => {
    if (
      !isResidency ||
      !isDesktopOrResidencyTablet ||
      resolvedResidencyMonth === null ||
      calendarEvents === null ||
      calendarEvents === undefined ||
      calendarEvents.isUpdating ||
      visibleMonths.length === 0
    ) {
      return;
    }

    const visibleResidencyMonth = visibleMonths.find((monthDate) =>
      dayjs(monthDate).isSame(resolvedResidencyMonth, "month"),
    );
    if (!visibleResidencyMonth) return;

    const monthKey = getMonthDateKey(visibleResidencyMonth);
    const eventsForMonth = deferredEventsByMonthAndDay[monthKey];
    if (!eventsForMonth || Object.keys(eventsForMonth).length === 0) return;

    const nextSnapshot = {
      monthDate: visibleResidencyMonth,
      monthKey,
      eventsByMonthAndDay: { [monthKey]: eventsForMonth },
      calendarEvents: {
        ...calendarEvents,
        isUpdating: false,
        isError: false,
      },
    };

    setResidencyCalendarSnapshot((previousSnapshot) => {
      if (
        previousSnapshot?.monthKey === nextSnapshot.monthKey &&
        previousSnapshot.eventsByMonthAndDay[nextSnapshot.monthKey] === eventsForMonth &&
        previousSnapshot.calendarEvents.pagedEvents === nextSnapshot.calendarEvents.pagedEvents &&
        previousSnapshot.calendarEvents.monthsWithEvents === nextSnapshot.calendarEvents.monthsWithEvents
      ) {
        return previousSnapshot;
      }

      return nextSnapshot;
    });
  }, [
    calendarEvents,
    deferredEventsByMonthAndDay,
    isDesktopOrResidencyTablet,
    isResidency,
    resolvedResidencyMonth,
    visibleMonths,
  ]);

  const resolvedResidencyMonthKey = resolvedResidencyMonth === null ? null : getMonthDateKey(resolvedResidencyMonth);
  const shouldRenderResidencySnapshot =
    isResidency &&
    isDesktopOrResidencyTablet &&
    resolvedResidencyMonthKey !== null &&
    residencyCalendarSnapshot !== null &&
    residencyCalendarSnapshot.monthKey !== resolvedResidencyMonthKey &&
    visibleMonths.length === 0;

  const renderVisibleMonths = shouldRenderResidencySnapshot ? [residencyCalendarSnapshot.monthDate] : visibleMonths;
  const renderEventsByMonthAndDay = shouldRenderResidencySnapshot
    ? residencyCalendarSnapshot.eventsByMonthAndDay
    : deferredEventsByMonthAndDay;
  const renderCalendarEvents = shouldRenderResidencySnapshot
    ? {
        ...residencyCalendarSnapshot.calendarEvents,
        isUpdating: true,
      }
    : calendarEvents;

  return isDesktopOrResidencyTablet ? (
    <>
      {isResidency && (
        <div className={containerStyle}>
          <ResidencyDesktopFilterBar
            availableMonths={residencyAvailableMonths}
            currentMonth={resolvedResidencyMonth}
            onMonthSelect={handleResidencyMonthSelect}
          />
        </div>
      )}
      {isResidency && resolvedResidencyMonth !== null && (
        <div className={containerStyle}>
          <ResidencyMonthNavigation
            currentMonth={resolvedResidencyMonth}
            availableMonths={residencyAvailableMonths}
            onPrev={handleResidencyPrev}
            onNext={handleResidencyNext}
          />
        </div>
      )}
      <CalendarHeader isResidency={isResidency} />
      <div className={containerStyle}>
        {renderVisibleMonths.length > 0 ? (
          renderVisibleMonths.map((monthDate, index) => (
            <CalendarMonth
              key={getMonthDateKey(monthDate)}
              index={index}
              monthDate={monthDate}
              eventsByMonthAndDay={renderEventsByMonthAndDay}
              renderMode={renderMode}
              calendarEvents={renderCalendarEvents}
              dates={isResidency ? null : filters.dates}
              categoryId={appContext.categoryId ?? 0}
              openNotificationHandler={openNotificationHandler}
              onEventClick={handleEventClick}
            />
          ))
        ) : (
          <CalendarSkeleton showNoResults={!calendarEvents?.isUpdating} resetFilters={handleResetFilters} />
        )}
        {!isResidency && calendarHasMoreMonths && (
          <div className={seeMoreContainer}>
            <Button
              classes={seeMoreButtonClasses}
              kind={ButtonKind.Neutral}
              variant={ButtonVariant.Outline}
              onClick={handleSeeMore}
              disabled={calendarEvents?.isUpdating}
            >
              {calendarEvents?.isUpdating ? (
                <Spinner />
              ) : (
                // "See more"
                <Content id="cid$#3983229#" />
              )}
            </Button>
          </div>
        )}
      </div>
      {notificationModal}
    </>
  ) : (
    <div className={mobileCalendarBody}>
      <MobileCalendar
        months={deferredMonths}
        eventsByMonthAndDay={deferredEventsByMonthAndDay}
        dates={filters.dates}
        categoryId={appContext.categoryId ?? 0}
        isUpdating={!!calendarEvents?.isUpdating}
        openNotificationHandler={openNotificationHandler}
        resetFilters={handleResetFilters}
      />
      {calendarHasMoreMonths && (
        <div className={bottomButtonContainer}>
          <Button
            classes={{ root: fullWidthButton }}
            kind={ButtonKind.Neutral}
            variant={ButtonVariant.Outline}
            onClick={handleSeeMore}
            disabled={calendarEvents?.isUpdating}
          >
            <Content id="cid$#3983229#" />
          </Button>
        </div>
      )}
      {notificationModal}
    </div>
  );
});
Calendar.displayName = "Calendar";
